'use client';

import { SignInButton, SignedIn, SignedOut, UserButton, useUser } from '@clerk/nextjs';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  LandingShell,
  Navbar,
  HeroWithProduct,
  HowItWorks,
  FeatureGrid,
  Pricing,
  FAQ,
  CTABanner,
  Footer,
} from '@vanmoose/landing-core';
import {
  Link as LinkIcon,
  ShieldCheck,
  Search,
  CalendarCheck,
  BarChart3,
  Mail,
  Download,
  Sparkles,
} from 'lucide-react';
import { RepetixLogo } from './components/RepetixLogo';

const navLinks = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
  { label: 'For Teams →', href: 'https://tracktron.cc' },
];

const features = [
  {
    icon: <LinkIcon className="w-5 h-5" />,
    title: 'One-click cancel',
    description:
      'Every subscription has a direct link to the cancel page. No hunting through settings. No dark patterns.',
  },
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: 'Never surprised again',
    description:
      'Get warned before they charge you. Choose 1–7 days ahead — whatever works for you.',
  },
  {
    icon: <Search className="w-5 h-5" />,
    title: '1,105 brands',
    description:
      'Type "Spot" and Spotify appears with logo, category, and cancel link. No manual entry required.',
  },
  {
    icon: <CalendarCheck className="w-5 h-5" />,
    title: 'Calendar sync',
    description:
      'Export to Google, Apple, or Outlook. See renewals where you actually look.',
  },
  {
    icon: <BarChart3 className="w-5 h-5" />,
    title: 'Spending breakdown',
    description:
      'Monthly, yearly, by category. The number you have been avoiding.',
  },
  {
    icon: <Mail className="w-5 h-5" />,
    title: 'Forward an email',
    description:
      'Forward any receipt or confirmation — AI extracts the details and adds it automatically.',
  },
  {
    icon: <Sparkles className="w-5 h-5" />,
    title: 'Auto-cancel detection',
    description:
      'Forward cancellation emails. We detect them and update your list automatically.',
  },
  {
    icon: <Download className="w-5 h-5" />,
    title: 'Export anytime',
    description:
      'Download as CSV. Works with Excel and Google Sheets. We do not lock you in.',
  },
];

const steps = [
  {
    number: '1',
    title: 'Add your subscriptions',
    description:
      'Type 3 letters, pick from 1,105 brands. Or forward a confirmation email — AI does the rest.',
  },
  {
    number: '2',
    title: 'See your total',
    description:
      'Monthly total. Yearly projection. Breakdown by category. Everything in one place.',
  },
  {
    number: '3',
    title: 'Stay in control',
    description:
      'Get reminded before charges. One-click cancel links. You decide what stays, what goes.',
  },
];

const faqItems = [
  {
    question: 'Do you connect to my bank?',
    answer:
      'No. We don’t connect to your bank or ask for bank credentials. You add subscriptions manually or by forwarding emails. Pro payments are securely processed by Stripe — we never see your card details.',
  },
  {
    question: 'What happens if I cancel Pro?',
    answer:
      'You keep Pro until the end of the billing period, then move to Free. You’ll only see your 3 most recent subscriptions — the rest stay stored safely. Upgrade again anytime to access them all.',
  },
  {
    question: 'How do reminders work?',
    answer:
      'We email you before each subscription renews. Choose anywhere from 1–7 days before — works for any billing cycle. You can customize per subscription.',
  },
  {
    question: 'Can I add subscriptions by email?',
    answer:
      'Yes! Forward any receipt or confirmation email to your personal Repetix address and AI extracts the details automatically. Forward cancellation emails too — we’ll mark those subscriptions as cancelled.',
  },
  {
    question: 'Can I upload a screenshot or photo of a receipt?',
    answer:
      'Yes! Upload a screenshot of a confirmation email, photo of a paper receipt, or image of your bank statement. AI reads the image and extracts service name, cost, and billing cycle automatically.',
  },
  {
    question: 'How do I track free trials?',
    answer:
      'Add them like any subscription with the trial end date. Get reminded before the trial converts to paid so you can decide: cancel or keep. Never get surprise-charged again.',
  },
  {
    question: 'Can I export my data?',
    answer:
      'Yes. Download all subscriptions as CSV anytime. Works with Excel, Google Sheets. We don’t lock you in.',
  },
  {
    question: 'Do you have a version for teams or businesses?',
    answer:
      'Yes! Our sister product Tracktron is built specifically for B2B SaaS spend management. Plans start at €49/month for teams. Visit tracktron.cc to learn more.',
  },
  {
    question: 'Can I sync to my calendar or ask Siri/ChatGPT?',
    answer:
      'Yes! Sync your subscriptions to Google Calendar, Apple Calendar, or Outlook. Then ask Siri, ChatGPT, or Claude: "What subscriptions are due this week?"',
  },
  {
    question: 'Is my data safe?',
    answer:
      'Yes. Industry-standard encryption, we never sell data, and you can delete your account completely anytime.',
  },
];

const footerColumns = [
  {
    title: 'Product',
    links: [
      { label: 'How it works', href: '/#how-it-works' },
      { label: 'Pricing', href: '/#pricing' },
      { label: 'FAQ', href: '/#faq' },
      { label: 'Docs', href: '/docs' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Subscription tracker', href: '/subscription-tracker' },
      { label: 'Free trial tracker', href: '/free-trial-tracker' },
      { label: 'Forgotten subscriptions', href: '/forgotten-subscriptions' },
      { label: 'Cancel subscriptions', href: '/cancel-subscriptions' },
    ],
  },
  {
    title: 'For Teams',
    links: [{ label: 'Tracktron →', href: 'https://tracktron.cc/business' }],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
      { label: 'Refund', href: '/refund' },
      { label: 'Help', href: '/help' },
    ],
  },
];

function HeroPriceCard() {
  return (
    <div className="relative max-w-md mx-auto">
      {/* Soft accent glow */}
      <div
        className="absolute -inset-6 opacity-40 blur-3xl rounded-[2rem]"
        style={{ background: 'var(--accent-glow)' }}
      />
      <div className="relative landing-card-premium text-center p-8">
        <div className="text-xs uppercase tracking-wider text-[var(--landing-text-muted)] mb-3">
          What you might really be paying
        </div>
        <div className="text-6xl md:text-7xl font-bold text-[var(--landing-text-primary)] mb-2">
          $3,744
          <span className="text-2xl text-[var(--landing-text-muted)] font-normal">
            /year
          </span>
        </div>
        <p className="text-sm text-[var(--landing-text-secondary)] mb-5">
          That’s what 14 subscriptions at $22/month adds up to.
        </p>
        <div className="flex items-center justify-center gap-4 text-sm text-[var(--landing-text-muted)] border-t border-[var(--landing-border)] pt-4">
          <span>14 subscriptions</span>
          <span>·</span>
          <span>$312/mo</span>
        </div>
      </div>
    </div>
  );
}

function AuthCTA() {
  return (
    <div className="flex items-center gap-3">
      <SignedOut>
        <SignInButton mode="modal">
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors duration-150"
          >
            Sign In
          </button>
        </SignInButton>
      </SignedOut>
      <SignedIn>
        <Link
          href="/dashboard"
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors duration-150 mr-2"
        >
          Dashboard
        </Link>
        <UserButton afterSignOutUrl="/" />
      </SignedIn>
    </div>
  );
}

function PricingSection() {
  const { isSignedIn } = useUser();
  const router = useRouter();

  const handleClick = (href: string) => {
    if (isSignedIn) router.push(href);
  };

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Track up to 3 subscriptions, every feature unlocked.',
      features: [
        'Track up to 3 subscriptions',
        'Email renewal reminders',
        'Calendar sync',
        'One-click cancel links',
      ],
      cta: 'Start free',
      href: '/dashboard',
    },
    {
      name: 'Pro',
      price: '$4.99',
      period: '/mo or $27/yr',
      description: 'Pays for itself if you catch one forgotten subscription.',
      features: [
        'Unlimited subscriptions',
        'Everything in Free',
        'Priority support',
        'Cancel anytime',
      ],
      cta: 'Get Pro',
      href: '/api/stripe/checkout',
      highlighted: true,
      badge: 'Most popular',
    },
  ];

  return (
    <section id="pricing" className="py-20">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <h2 className="headline-lg mb-4">
            Less than one forgotten subscription.
          </h2>
          <p className="body-lg">
            If Repetix saves you from forgetting one $10 subscription, it pays
            for itself twice.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan) => {
            const panelStyles = `relative flex flex-col rounded-2xl p-8 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-xl ${
              plan.highlighted
                ? 'bg-[var(--accent)]/10 border-2 border-[var(--accent)]'
                : 'bg-[var(--landing-bg-card)] border border-[var(--landing-border)]'
            }`;

            const panelContent = (
              <>
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--accent)] text-white shadow-lg whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-[var(--landing-text-muted)] mb-4">
                    {plan.description}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-[var(--landing-text-primary)]">
                      {plan.price}
                    </span>
                    <span className="text-sm text-[var(--landing-text-muted)]">
                      {plan.period}
                    </span>
                  </div>
                </div>
                <ul className="flex-1 space-y-3 mb-6">
                  {plan.features.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm text-[var(--landing-text-secondary)]"
                    >
                      <svg
                        className="h-4 w-4 text-[var(--accent)] flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <div
                  className={`w-full py-3 rounded-lg text-sm font-semibold text-center ${
                    plan.highlighted
                      ? 'bg-[var(--accent)] text-white'
                      : 'bg-[var(--landing-bg-hover)] border border-[var(--landing-border-strong)] text-[var(--landing-text-primary)]'
                  }`}
                >
                  {plan.cta}
                </div>
              </>
            );

            if (!isSignedIn) {
              return (
                <SignInButton
                  key={plan.name}
                  mode="modal"
                  forceRedirectUrl={plan.href}
                >
                  <div className={panelStyles} role="button" tabIndex={0}>
                    {panelContent}
                  </div>
                </SignInButton>
              );
            }

            return (
              <div
                key={plan.name}
                className={panelStyles}
                onClick={() => handleClick(plan.href)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleClick(plan.href)}
              >
                {panelContent}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <div className="inline-flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm text-[var(--landing-text-muted)]">
            <span>No bank connection</span>
            <span>·</span>
            <span>We never sell data</span>
            <span>·</span>
            <span>Delete anytime</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Page() {
  return (
    <LandingShell theme="repetix">
      <Navbar
        logo={
          <div className="flex items-center gap-2.5">
            <RepetixLogo size="lg" />
            <span className="font-semibold text-[var(--landing-text-primary)]">
              Repetix
            </span>
          </div>
        }
        links={navLinks}
        ctaElement={<AuthCTA />}
      />

      <HeroWithProduct
        badge={{ text: 'Free • 3 subscriptions • No credit card', href: '/dashboard' }}
        headline={
          <>
            How many <span className="text-[var(--accent)]">subscriptions</span> are you really paying for?
          </>
        }
        subheadline="Most people underestimate the number. Yours might surprise you. See the total, get reminded before each renewal, cancel in one click."
        primaryCTA={{
          text: 'See what you’re wasting in 60 seconds',
          href: '/dashboard',
        }}
        secondaryCTA={{ text: 'How it works', href: '#how-it-works' }}
        productShowcase={<HeroPriceCard />}
      />

      <HowItWorks
        heading="How it works"
        subheading="Three steps. Less than a minute. Full control."
        steps={steps}
      />

      <FeatureGrid
        id="how-it-works"
        heading="Everything you need. Nothing you don’t."
        subheading="Simple tools that make tracking subscriptions effortless."
        features={features}
        columns={4}
      />

      <PricingSection />

      <FAQ id="faq" heading="Questions?" items={faqItems} />

      <CTABanner
        heading="Ready to see the number?"
        subheading="Free forever for 3 subscriptions. Set up in under a minute."
        primaryCTA={{
          text: 'See what you’re wasting in 60 seconds',
          href: '/dashboard',
        }}
      />

      <Footer
        logo={
          <div className="flex items-center gap-2.5">
            <RepetixLogo size="lg" />
            <span className="font-semibold text-[var(--landing-text-primary)]">
              Repetix
            </span>
          </div>
        }
        columns={footerColumns}
        bottomLinks={[
          { label: 'Privacy', href: '/privacy' },
          { label: 'Terms', href: '/terms' },
        ]}
      />
    </LandingShell>
  );
}
